import { put, takeLatest, call } from 'redux-saga/effects';
import {
  GET_NUTRITION_SUGGESTION,
  GET_COACHING_MESSAGE,
  GET_ASSESSMENT_SUMMARY,
} from './constants';
import {
  getNutritionSuggestionSuccess,
  getNutritionSuggestionError,
  getCoachingMessageSuccess,
  getCoachingMessageError,
  getCoachingMessage as getCoachingMessageAction,
  getAssessmentSummarySuccess,
  getAssessmentSummaryError,
  getAssessmentSummary as getAssessmentSummaryAction,
} from './actions';
import { fetchData } from '../api';

/**
 * Worker for the nutrition smart-suggestion fetch. Uses `takeLatest` so a
 * burst of dispatches (e.g. from rapid food-log writes) collapses to the
 * most recent — older in-flight requests are cancelled.
 */
export function* getNutritionSuggestion() {
  try {
    const url = `${process.env.REACT_APP_API_URL}/api/ai/nutrition/suggestion`;
    const config = {
      headers: {
        'x-auth-token': window.localStorage.token,
        'Content-Type': 'application/json',
      },
    };
    const payload = yield call(fetchData, url, config);
    yield put(getNutritionSuggestionSuccess(payload));
  } catch (err) {
    let error = err;
    try {
      error = yield err.json();
    } catch (_) {
      // err wasn't a Response — fall through with the original.
    }
    yield put(getNutritionSuggestionError(error));
  }
}

export function* getCoachingMessage() {
  try {
    const url = `${process.env.REACT_APP_API_URL}/api/ai/coaching/daily-message`;
    const config = {
      headers: {
        'x-auth-token': window.localStorage.token,
        'Content-Type': 'application/json',
      },
    };
    const payload = yield call(fetchData, url, config);
    yield put(getCoachingMessageSuccess(payload));
  } catch (err) {
    let error = err;
    try {
      error = yield err.json();
    } catch (_) {
      // not a Response
    }
    yield put(getCoachingMessageError(error));
  }
}

/**
 * Trainer-side: fetch the AI briefing for a specific client. The server-
 * side cache key is content-hashed, so a trainer who just saved an
 * assessment change will get a fresh summary on the next fetch (the
 * hash shifted, cache miss, regenerate). No invalidate() needed.
 */
export function* getAssessmentSummary(action) {
  const { clientId } = action;
  try {
    if (!clientId) {
      throw new Error('clientId is required');
    }
    const url = `${process.env.REACT_APP_API_URL}/api/ai/client/${clientId}/assessment-summary`;
    const config = {
      headers: {
        'x-auth-token': window.localStorage.token,
        'Content-Type': 'application/json',
      },
    };
    const payload = yield call(fetchData, url, config);
    yield put(getAssessmentSummarySuccess(payload, clientId));
  } catch (err) {
    let error = err;
    try {
      error = yield err.json();
    } catch (_) {
      // not a Response
    }
    yield put(getAssessmentSummaryError(error, clientId));
  }
}

// Re-fetch handlers — when a user action changes the inputs the AI
// pipelines read from, dispatch the corresponding fetch so the next
// home render reflects the new data immediately. The server-side cache
// has already been invalidated by the mutation; this triggers the
// recompute.
function* refreshCoachingMessage() {
  yield put(getCoachingMessageAction());
}

/**
 * Profile save fired — refresh the AI assessment briefing for the
 * profile that was saved. Catches both:
 *   • Trainer saving a client's macros / assessment (via Profile saga
 *     paths that fire `saveProfileSuccess` with the client's profile)
 *   • Client saving their own profile (same action, own profile)
 *
 * The summary slot is keyed by clientId, so a stale dispatch for a
 * profile that no V2 page is currently viewing is a harmless no-op
 * (the reducer ignores success payloads for non-current clientIds).
 */
function* refreshAssessmentSummaryOnSave(action) {
  const clientId = action?.payload?.profile?.user;
  if (!clientId) return;
  yield put(getAssessmentSummaryAction(clientId));
}

export default function* aiSaga() {
  yield takeLatest(GET_NUTRITION_SUGGESTION, getNutritionSuggestion);
  yield takeLatest(GET_COACHING_MESSAGE, getCoachingMessage);
  yield takeLatest(GET_ASSESSMENT_SUMMARY, getAssessmentSummary);
  // Workouts are the strongest signal for coaching; re-fetch on success.
  yield takeLatest(
    'workout/CREATE_USER_GENERATED_WORKOUT_SUCCESS',
    refreshCoachingMessage
  );
  yield takeLatest('workout/CREATE_WORKOUT_SUCCESS', refreshCoachingMessage);
  // Check-ins influence tone (low energy / high stress / etc.).
  yield takeLatest('checkIn/SUBMIT_SUCCESS', refreshCoachingMessage);
  // Trainer saves a client's macros or assessment → refresh the AI
  // briefing for that client. The server-side cache is content-hashed
  // so we'd auto-regenerate on the NEXT page mount, but firing here
  // means the Assessment tab updates immediately without a navigation.
  yield takeLatest(
    'profile/SAVE_PROFILE_SUCCESS',
    refreshAssessmentSummaryOnSave
  );
  // Note: food-log writes happen primarily on mobile, not via this saga.
  // When the web app gains a food-log POST flow, add a refresh trigger
  // here pointing at the success constant.
}
